import { yupResolver } from '@hookform/resolvers/yup';
import { InterlacingSection, Spacer } from '@insify/components';
import { Button, Modal } from '@insify/design-system/components';
import {
  PartnershipExplained as BasePartnershipExplained,
  Layout,
  RichContent,
  WizardLayoutForm,
  useProspect,
  useWizard,
} from '@insify/funnel-components';
import { formatMoney } from '@insify/global-money';
import { formatMoneyPlaceholder } from '@insify/ui-utils';
import { captureMessage } from '@sentry/react';
import { addDays, format } from 'date-fns';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { StripeElementsProvider, useStripePayment } from '../../components/StripeProvider';
import { useCoBranding } from '../../hooks/useCoBranding';
import { useCommands } from '../../hooks/useCommands';
import {
  invokeCheckoutQuery,
  invokeStripeIntentQuery,
  useCheckoutQuery,
  useConditionsQuery,
  useParamsQuery,
  useStripeIntentQuery,
} from '../../hooks/useQueries';
import { stepsContext } from '../../hooks/useSteps';

import styles from './Checkout.module.css';
import { Company } from './components/Company';
import { Consents } from './components/Consents';
import { Overview } from './components/Overview';
import { Payment } from './components/Payment';
import { StartDate } from './components/StartDate';
import { USPs } from './components/USPs';
import { validation } from './validation';

import type { PaymentMethod, PaymentWidgetData } from '../../models/queries';
import type { SubmitHandler } from 'react-hook-form';

export type CheckoutFormType = {
  startDate: string;
  iban: string;
  paymentMethod: PaymentMethod;
};

export const PartnershipExplained: React.FC = () => {
  const { partnershipExplained } = useCoBranding();
  return <BasePartnershipExplained content={partnershipExplained} />;
};

const Section = ({ children, ...props }: React.ComponentProps<typeof InterlacingSection>) => (
  <InterlacingSection {...props}>{children}</InterlacingSection>
);

const CheckoutForm = () => {
  const { t } = useTranslation(['common', 'validation', 'funnel']);
  const { showNextPage, showPreviousPage } = useWizard();
  const { sendCommand, sendMultipleCommands, isLoading } = useCommands();
  const { confirmStripeIntent, paymentState } = useStripePayment();
  const { data: stripeIntent } = useStripeIntentQuery();
  const { partnershipExplained } = useCoBranding();
  const { data: params } = useParamsQuery();
  const { data: conditions } = useConditionsQuery();
  const { data: checkoutData } = useCheckoutQuery();
  const defaultPaymentMethod =
    conditions?.paymentMethods?.length === 1 ? conditions?.paymentMethods[0] : params?.paymentMethod;
  const dateBoundaries = conditions?.startDateBoundariesInDays;
  const startDate = params?.startDate || format(addDays(new Date(), dateBoundaries?.[0] || 0), 'yyyy-MM-dd');
  const form = useForm<CheckoutFormType>({
    defaultValues: {
      startDate,
      paymentMethod: defaultPaymentMethod,
    },
    resolver: yupResolver(validation(t)),
  });
  const prospectId = useProspect();
  const [triggerShowingNextPage, setTriggerShowingNextPage] = useState(false);
  const [errorModal, setErrorModal] = useState<{ message: string; link: string } | undefined>();
  const { setHasSubmittedApplication } = stepsContext.useHook();
  const hasFinishedPayment = stripeIntent?.status === 'succeeded';

  useEffect(() => {
    if (triggerShowingNextPage) {
      showNextPage();
    }
  }, [triggerShowingNextPage]);

  useEffect(() => {
    void sendCommand({ type: 'requestQuote', payload: {} });
  }, []);

  useEffect(() => {
    if (paymentState.state === 'error') {
      switch (paymentState.data.code) {
        case 'payment_intent_unexpected_state': {
          captureMessage(
            `Payment intent in unexpected state: ${paymentState.data.payment_intent?.status}; This might mean that the user has already paid but could click submit button again`,
            { level: 'warning' },
          );
          break;
        }
        case 'setup_intent_unexpected_state': {
          captureMessage(
            `Setup intent in unexpected state: ${paymentState.data.setup_intent?.status}; This might mean that the user has already paid but could click submit button again`,
            { level: 'warning' },
          );
          break;
        }
      }
    }

    if (paymentState.state === 'success') {
      void (async () => {
        // ideally, we should use Stripe webhook to trigger submitApplication
        // but since we expect to deprecate Stripe when we move to Socotra, the effort is saved for now - and we scaffold the command here
        await sendCommand({
          type: 'submitApplication',
          payload: {},
        });

        setHasSubmittedApplication(true);
        setTriggerShowingNextPage(true);
      })();
    }
  }, [paymentState]);

  const assertPaymentIntentStillValid = async () => {
    try {
      await invokeStripeIntentQuery(prospectId);
    } catch (err) {
      setErrorModal({
        message: t('funnel:checkout_error_modal_description_refresh'), // "please start all over"
        link: 'https://shop.insify.fr/fr/checkout',
      });
      throw new Error('Price might have changed - please refresh the page');
    }
  };

  const isReadyToSubmit = async () => {
    const { readyToSubmit } = await invokeCheckoutQuery(prospectId);
    return readyToSubmit;
  };

  const selectPolicyStartDateWithErrorFeedback = async (startDate: string) => {
    try {
      // send all commands except submitApplication, to fulfill the validation below on `readyToSubmit`
      await sendCommand({
        type: 'selectPolicyStartDate',
        payload: { startDate },
      });
    } catch (err) {
      form.setError('startDate', { type: 'manual', message: 'Invalide' });
      throw err;
    }
  };

  const assertReadyToSubmitWithErrorFeedback = async () => {
    const readyToSubmit = await isReadyToSubmit();
    if (!readyToSubmit) {
      setErrorModal({
        message: t('funnel:checkout_error_modal_description_start_over_again'), // "please start all over"
        link: 'https://shop.insify.fr/fr/activity',
      });
      throw new Error('Missing info - please start from the first page and go over again');
    }
  };

  const confirmStripeIntentWithErrorFeedback = async (intent: PaymentWidgetData) => {
    try {
      await confirmStripeIntent?.(intent);
    } catch (err) {
      form.setError('paymentMethod', { type: 'manual', message: String(err) });
      throw err;
    }
  };

  const submitWithCreditCard = async () => {
    if (!stripeIntent?.intentType) {
      throw new Error('No intent type known for payment');
    }

    await sendCommand({
      type: 'enterCreditCardDetails',
      payload: {},
    });
    await assertReadyToSubmitWithErrorFeedback();

    await confirmStripeIntentWithErrorFeedback(stripeIntent);
  };

  const submitWithSepa = async (formData: CheckoutFormType) => {
    await sendCommand({
      type: 'enterBankDetails',
      payload: {
        iban: formData.iban,
      },
    });
    await assertReadyToSubmitWithErrorFeedback();

    await sendCommand({
      type: 'submitApplication',
      payload: {},
    });

    setHasSubmittedApplication(true);
    setTriggerShowingNextPage(true);
  };

  const submit: SubmitHandler<CheckoutFormType> = async (formData) => {
    await assertPaymentIntentStillValid();

    if (!formData.paymentMethod) {
      return;
    }

    // triggering validation of startDate BEFORE payment - otherwise the user could finish paying while the commands fail
    await selectPolicyStartDateWithErrorFeedback(formData.startDate);

    await sendMultipleCommands([
      {
        type: 'giveConsents',
        payload: {
          consents: [
            {
              type: 'payment',
              statement: t('funnel:checkout_important_information_payment'),
            },
            {
              type: 'electronicCommunication',
              statement: t('funnel:checkout_important_information_documents'),
            },
          ],
        },
      },
    ]);

    if (formData.paymentMethod === 'creditCard') {
      await submitWithCreditCard();
    } else {
      await submitWithSepa(formData);
    }
  };

  const amountOrPlaceholder = checkoutData?.totalPayableCheckout
    ? formatMoney(checkoutData.totalPayableCheckout, 'fr')
    : formatMoneyPlaceholder('EUR', 'fr');

  return (
    <>
      {errorModal?.message && (
        <Modal trackingId='checkout-error-modal' open={!!errorModal?.message} onClose={() => setErrorModal(undefined)}>
          <RichContent.H2>{t('funnel:checkout_error_modal_title')}</RichContent.H2>
          <RichContent.P>{errorModal.message}</RichContent.P>
          <Button
            className={styles.errorModalButton}
            trackingId='checkout-error-modal-redirect'
            kind='primary'
            href={errorModal.link}
          >
            {t('funnel:checkout_error_modal_button')}
          </Button>
        </Modal>
      )}

      <FormProvider {...form}>
        <WizardLayoutForm
          isSubmitting={isLoading || paymentState.state === 'loading'}
          backCopy={t('common:back')}
          onBack={showPreviousPage}
          forwardCopy={`Payer ${amountOrPlaceholder}`}
          isForwardDisabled={paymentState.state === 'initializing'}
          onSubmit={form.handleSubmit(submit)}
        >
          <Section contentClassName={styles.firstSection}>
            <Overview />
            <Spacer />
            <Company />
          </Section>
          {partnershipExplained && (
            <Section>
              <PartnershipExplained />
            </Section>
          )}
          <Section>
            <Consents />
          </Section>
          <Section>
            <StartDate />
          </Section>
          <Section contentClassName={styles.lastSection}>
            {!hasFinishedPayment && <Payment paymentMethods={conditions?.paymentMethods} />}
            <USPs className={styles.usp} />
            <Layout.DesktopOnly>
              <Layout.SubmitFullWidth
                submitCopy={hasFinishedPayment ? 'Finir' : `Payer ${amountOrPlaceholder}`}
                className={styles.submitButton}
              />
            </Layout.DesktopOnly>
          </Section>
        </WizardLayoutForm>
      </FormProvider>
    </>
  );
};

export const CheckoutPage = () => (
  <StripeElementsProvider>
    <CheckoutForm />
  </StripeElementsProvider>
);
